/**
 * StockCountListPage — Leltár lista (Epic 24, Q-T109 fázis-1: route + lista)
 *
 * A `/inventory/stock-count` route korábban a `/inventory/:id` catch-all-ba esett
 * (":id" = "stock-count") → rossz hívás → HTTP 400. A BE `GET /api/v1/stock-counts`
 * kész és 200-at ad (StockCountController#listStockCounts, #833 óta wired) — ez a
 * dedikált FE-lap köti be.
 *
 * Fázis-1 scope: route + lista + empty/error-state. Az indítás (POST) + rögzítés
 * fázis-2 (később) — az "Új leltár" gomb egyelőre disabled placeholder.
 *
 * #886 empty≠error-minta: az üres lista (200 + [] válasz) és a fetch-hiba KÜLÖN
 * render-ágban jelenik meg (nem keverednek).
 */

import type { StockCountStatus } from '@/api/inventory';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useStockCounts } from '@/hooks/use-inventory';
import { useTranslation } from '@kgc/i18n';

const STATUS_LABEL_KEYS: Record<StockCountStatus, string> = {
  DRAFT: 'inventory.stockCount.statusLabels.draft',
  IN_PROGRESS: 'inventory.stockCount.statusLabels.inProgress',
  SUSPENDED: 'inventory.stockCount.statusLabels.suspended',
  PENDING_REVIEW: 'inventory.stockCount.statusLabels.pendingReview',
  PENDING_ADJUSTMENT: 'inventory.stockCount.statusLabels.pendingAdjustment',
  COMPLETED: 'inventory.stockCount.statusLabels.completed',
  CANCELLED: 'inventory.stockCount.statusLabels.cancelled',
};

const STATUS_BADGE_CLASS: Record<StockCountStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  SUSPENDED: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
  PENDING_REVIEW: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  PENDING_ADJUSTMENT: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function StockCountListPage() {
  const { t } = useTranslation();
  const { stockCounts, isLoading, error, refetch } = useStockCounts();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('inventory.stockCount.title')}</h1>
        {/* TODO(Q-T109 fázis-2): leltár indítás (POST /stock-counts) — most még disabled placeholder */}
        <Button disabled title={t('inventory.stockCount.newButtonHint')}>
          {t('inventory.stockCount.newButton')}
        </Button>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && error && (
        <Card
          data-testid="stock-count-error"
          className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
        >
          <CardContent className="py-8 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">
              {t('page.errorPrefix', { message: error })}
            </p>
            <Button onClick={() => refetch()}>{t('common.retry')}</Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && stockCounts.length === 0 && (
        <Card data-testid="stock-count-empty">
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('inventory.stockCount.empty')}
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && stockCounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('inventory.stockCount.title')} ({stockCounts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div data-testid="stock-count-list" className="divide-y">
              <div className="hidden md:grid grid-cols-5 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground uppercase">
                <span>{t('inventory.stockCount.columns.countNumber')}</span>
                <span>{t('inventory.stockCount.columns.name')}</span>
                <span>{t('inventory.stockCount.columns.status')}</span>
                <span>{t('inventory.stockCount.columns.createdAt')}</span>
                <span>{t('inventory.stockCount.columns.warehouse')}</span>
              </div>
              {stockCounts.map(sc => (
                <div
                  key={sc.id}
                  data-testid="stock-count-row"
                  className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-3 items-center"
                >
                  <span className="font-medium">{sc.countNumber}</span>
                  <span>{sc.name}</span>
                  <span>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[sc.status]}`}
                    >
                      {t(STATUS_LABEL_KEYS[sc.status])}
                    </span>
                  </span>
                  <span className="text-sm text-muted-foreground">{formatDate(sc.createdAt)}</span>
                  <span className="text-sm text-muted-foreground">{sc.warehouseId}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
